import {
  SLICE_OUTPUT_CAP_BYTES,
  SliceOsfResult,
  estimateSliceSizeBytes,
} from '@ouster/osf-slice';
import {
  DownloadSliceOsfProps,
  SliceFrameRange,
  sanitizeFileName,
} from '@ouster/webviz-react-components';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import useChakraHook from './useChakraHook';
import { getOSFWorker } from '../getOSFWorker';
import { extractOSFPath } from '../containers/DriveViewer.utils';
import { DriveOsfInfo, RawDriveOsfInfo } from '../reducers/drive';
import { OutputFileInfo } from '../types';
import { downloadBlob } from '../utils/downloadBlob';
import { getSignedUrl } from '../utils/requests';

const OSF_INFO_FILENAME = 'osf_info.json';

/** Frame counts offered as one-click "first N frames" presets. */
const FRAME_PRESETS = [10, 20, 30];

/** Frame count a freshly opened slice section starts with. */
const DEFAULT_SLICE_FRAMES = 10;

/**
 * What the popover needs to know about a recording's sliceable OSF, once
 * its osf_info.json has been read.
 */
type SliceSource = {
  /**
   * Recording this was read for. Kept on the record so the hook can tell
   * whether it still describes the recording being shown, rather than
   * clearing it from an effect when the popover closes or moves on - which
   * would leave a render showing the previous recording's frame count.
   */
  driveUuid: string;
  /** Drive-relative path of the OSF to slice, for signing at click time. */
  osfPath: string;
  /** Frames in that OSF - the range ceiling, and the estimate denominator. */
  totalFrames: number;
};

/**
 * Finds the listed download entry for the OSF that osf_info points at, so
 * its byte size can size the estimate without an extra request.
 *
 * Matched leniently because the two sides describe the same blob by
 * different conventions: osf_info gives a path relative to the drive's OSF
 * prefix, while the listing's `path` is whatever the server chopped the
 * blob name down to. The basename is the reliable common ground - a
 * section's OSF is always named after the section.
 * @param files output files listed in the popover
 * @param osfPath drive-relative OSF path from osf_info
 * @returns the matching file, or undefined
 */
const findListedOsf = (files: OutputFileInfo[], osfPath: string) => {
  const basename = osfPath.split('/').pop();
  return files.find((file) => {
    if (file.type !== 'osf') return false;
    const filePath = file.path.replace(/^\//, '');
    return (
      osfPath === filePath ||
      osfPath.endsWith(filePath) ||
      filePath.endsWith(osfPath) ||
      file.filename === basename ||
      filePath.split('/').pop() === basename
    );
  });
};

interface WorkerSliceMessage extends MessageEvent {
  data: {
    command?: string;
    requestId?: number;
    result?: SliceOsfResult;
    fraction?: number;
  };
}

/**
 * Posts a `'slice'` command to the OSF worker and resolves with the
 * matching `'slice-result'`, correlated by requestId so a stale reply
 * can't settle a newer request. Intermediate `'slice-progress'` messages
 * are forwarded to onProgress instead of resolving.
 * @param worker OSF worker to slice on
 * @param params slice request params
 * @param onProgress called with a rough completion fraction (0-1)
 * @returns the slice result posted back by the worker
 */
function requestSliceFromWorker(
  worker: Worker,
  params: {
    requestId: number;
    url: string;
    fileSizeBytes: number;
    totalFrames: number;
    sliceStartIdx: number;
    sliceEndIdx: number;
  },
  onProgress: (fraction: number) => void
): Promise<SliceOsfResult> {
  return new Promise((resolve) => {
    const handleMessage = (e: WorkerSliceMessage) => {
      const { data } = e;
      if (!data || data.requestId !== params.requestId) return;

      if (data.command === 'slice-progress') {
        if (typeof data.fraction === 'number') onProgress(data.fraction);
        return;
      }

      if (data.command !== 'slice-result' || !data.result) return;
      worker.removeEventListener('message', handleMessage as EventListener);
      resolve(data.result);
    };
    worker.addEventListener('message', handleMessage as EventListener);
    worker.postMessage({ command: 'slice', ...params });
  });
}

export interface UseDownloadSliceOsfParams {
  /** Recording the download popover is currently showing, if any. */
  driveUuid?: string;
  orgId?: number;
  isPublic: boolean;
  /** Output files listed in the popover, used to size the OSF. */
  downloadableOutputFiles: OutputFileInfo[];
  recordingTitle?: string;
  /** Whether the popover is currently open. */
  isOpen: boolean;
  /** Output-size cap in bytes, defaults to {@link SLICE_OUTPUT_CAP_BYTES}. */
  capBytes?: number;
}

/**
 * Owns everything behind the download popover's "Slice OSF" section:
 * reading the recording's frame count without opening it, the selected
 * frame range, and the slice-and-download action itself.
 *
 * The data-app twin of ouster-viewer's hook of the same name - the WASM
 * work is identical (both go through the shared `sliceCloudOsf` in the
 * same worker protocol), and only the delivery differs: a Blob and an
 * `<a download>` here, where ouster-viewer writes to disk over IPC. Kept
 * as a per-app copy rather than shared code, matching how the two apps
 * already carry their own `useSliceOrchestration`.
 *
 * Frames only, no thumbnails, no filename field - a downloads list has no
 * viewport to scrub and no place for a rename step.
 * @param params see UseDownloadSliceOsfParams
 * @returns `sliceOsf` props for RecordingDownloadPopover, or undefined
 * when this recording has nothing sliceable
 */
export function useDownloadSliceOsf({
  driveUuid,
  orgId,
  isPublic,
  downloadableOutputFiles,
  recordingTitle,
  isOpen,
  capBytes = SLICE_OUTPUT_CAP_BYTES,
}: UseDownloadSliceOsfParams): DownloadSliceOsfProps | undefined {
  const { showToast } = useChakraHook();
  const [loadedSource, setLoadedSource] = useState<SliceSource | null>(null);
  const [range, setRange] = useState<SliceFrameRange>({
    startFrame: 0,
    endFrame: DEFAULT_SLICE_FRAMES,
  });
  const [slicingDriveUuid, setSlicingDriveUuid] = useState<string | null>(null);
  const [progressFraction, setProgressFraction] = useState<number | null>(null);
  const isSlicing = slicingDriveUuid !== null && slicingDriveUuid === driveUuid;
  const isBlockedByOtherSlice =
    slicingDriveUuid !== null && slicingDriveUuid !== driveUuid;

  // Created on the first slice rather than on mount: this is a recordings
  // list, and spinning up a worker plus instantiating the WASM SDK is a
  // real cost to pay for a page most visits never slice from.
  const workerRef = useRef<Worker | null>(null);
  const requestIdRef = useRef(0);

  // The slice currently being produced, if any. Its id is reserved before
  // the first await in onDownload, so a Cancel arriving during the
  // signed-URL round trip can't post an id the worker has never seen (and
  // be silently dropped); the `cancelled` flag covers that same window
  // from the other side, since there is no worker job to abort yet.
  const activeJobRef = useRef<{ requestId: number; cancelled: boolean } | null>(
    null
  );

  const getWorker = useCallback(() => {
    if (!workerRef.current) {
      workerRef.current = getOSFWorker();
    }
    return workerRef.current;
  }, []);

  useEffect(
    () => () => {
      workerRef.current?.terminate();
      workerRef.current = null;
    },
    []
  );

  // Read the recording's frame count from osf_info.json - the popover
  // never opens the recording, so there's no loaded OSF to ask, and
  // probing the file itself would mean booting WASM just to render a
  // number. Only the first section (ids[0]) is sliceable.
  const source =
    isOpen && loadedSource?.driveUuid === driveUuid ? loadedSource : null;

  useEffect(() => {
    if (!isOpen || !driveUuid) return () => {};

    let cancelled = false;

    const loadSource = async () => {
      const signedInfoUrl = await getSignedUrl(
        OSF_INFO_FILENAME,
        driveUuid,
        orgId,
        !isPublic
      );
      const response = await fetch(signedInfoUrl);
      if (!response.ok) {
        throw new Error(`Bad response for ${OSF_INFO_FILENAME}`);
      }

      const raw = (await response.json()) as RawDriveOsfInfo;
      const osfInfo: DriveOsfInfo = {
        ids: raw.ids,
        configById: raw.config_by_id,
        offsetsById: raw.offsets_by_id,
      };

      const sectionId = osfInfo.ids[0];
      const config = sectionId ? osfInfo.configById[sectionId] : undefined;
      if (!config?.numFrames || cancelled) return;

      setLoadedSource({
        driveUuid,
        osfPath: extractOSFPath(osfInfo, sectionId),
        totalFrames: config.numFrames,
      });
      setRange({
        startFrame: 0,
        endFrame: Math.min(DEFAULT_SLICE_FRAMES, config.numFrames),
      });
    };

    loadSource().catch((error) => {
      // A recording whose OSF isn't ready (or is too old to have one) just
      // doesn't offer slicing - not worth a toast on top of a popover the
      // user opened to download something else.
      console.warn('Slice OSF unavailable for this recording', error);
    });

    return () => {
      cancelled = true;
    };
  }, [isOpen, driveUuid, isPublic, orgId]);

  // Derived rather than captured when osf_info loads: the popover opens
  // first and loads its file listing afterwards, so this is routinely
  // still empty on the effect's first pass and only fills in a moment later
  const fileSizeBytes = useMemo(() => {
    if (!source) return null;
    return findListedOsf(downloadableOutputFiles, source.osfPath)?.size ?? null;
  }, [downloadableOutputFiles, source]);

  // A listing that arrived but doesn't contain the OSF osf_info points at
  // means the two sides disagree about how that blob is named - worth
  // saying out loud with both spellings, since the section just silently
  // won't appear.
  useEffect(() => {
    if (!source || fileSizeBytes !== null) return;
    const listedOsfPaths = downloadableOutputFiles
      .filter((file) => file.type === 'osf')
      .map((file) => file.path);
    if (listedOsfPaths.length === 0) return;
    console.warn(
      `Slice OSF: no listed file matches ${source.osfPath}`,
      listedOsfPaths
    );
  }, [source, fileSizeBytes, downloadableOutputFiles]);

  const sizeEstimateBytes = useMemo(() => {
    if (!source) return null;
    return estimateSliceSizeBytes(
      fileSizeBytes,
      source.totalFrames,
      range.startFrame,
      // The section's fields are end-exclusive ("0 - 10" is the first 10
      // frames); the slice API's end index is inclusive
      range.endFrame - 1
    );
  }, [source, fileSizeBytes, range]);

  const onCancel = useCallback(() => {
    const job = activeJobRef.current;
    if (!job) return;

    job.cancelled = true;
    // Harmless if the worker hasn't started this id yet (it ignores an
    // unknown one); onDownload's own cancelled checks handle that window.
    workerRef.current?.postMessage({
      command: 'cancel-slice',
      requestId: job.requestId,
    });
  }, []);

  const onDownload = useCallback(async () => {
    if (!source || fileSizeBytes === null || !driveUuid) return;

    // One slice at a time: each one holds its whole output in the WASM
    // module's linear memory (that's what the size cap exists for), so two
    // at once could exceed the ceiling even though each is individually
    // under the cap
    if (slicingDriveUuid !== null) {
      showToast({
        title: 'A slice is already in progress',
        description: 'Wait for it to finish, or cancel it first.',
        status: 'info',
      });
      return;
    }

    const frameCount = range.endFrame - range.startFrame;
    const baseName = sanitizeFileName(recordingTitle || '') || 'recording';
    const fileName = `${baseName}_${range.startFrame}frm_${
      range.endFrame - 1
    }frm_slice.osf`;

    requestIdRef.current += 1;
    const job = { requestId: requestIdRef.current, cancelled: false };
    activeJobRef.current = job;
    setSlicingDriveUuid(driveUuid);
    setProgressFraction(0);
    try {
      // Signed at click time rather than when the popover opened, so a
      // long-running slice starts with the URL's full lifetime ahead of it
      const signedOsfUrl = await getSignedUrl(
        source.osfPath,
        driveUuid,
        orgId,
        !isPublic
      );
      // Cancelled while signing - nothing has been handed to the worker
      // yet, so there is nothing to abort, just don't start
      if (job.cancelled) return;
      if (!signedOsfUrl) {
        throw new Error('Could not get a download URL for this recording');
      }

      const result = await requestSliceFromWorker(
        getWorker(),
        {
          requestId: job.requestId,
          url: signedOsfUrl,
          fileSizeBytes,
          totalFrames: source.totalFrames,
          sliceStartIdx: range.startFrame,
          sliceEndIdx: range.endFrame - 1,
        },
        (fraction) => {
          if (!job.cancelled) setProgressFraction(fraction);
        }
      );

      // Cancelled while slicing, or between the result arriving and the
      // download: either way the user asked for no file, so don't hand
      // them one
      if (job.cancelled) return;

      if (!result.ok) {
        if (result.cancelled) return;
        showToast({
          title: result.expired
            ? 'Recording link expired'
            : 'Failed to slice recording',
          description: result.expired
            ? 'Close and reopen the download menu, then try again.'
            : result.error,
          status: 'error',
        });
        return;
      }

      downloadBlob(result.bytes, fileName);
      showToast({
        title: `Slice ready (${frameCount} frame${
          frameCount === 1 ? '' : 's'
        })`,
        description: fileName,
        status: 'success',
      });
    } catch (error) {
      showToast({
        title: 'Failed to slice recording',
        description: error instanceof Error ? error.message : String(error),
        status: 'error',
      });
    } finally {
      if (activeJobRef.current === job) {
        activeJobRef.current = null;
      }
      setSlicingDriveUuid(null);
      setProgressFraction(null);
    }
  }, [
    source,
    fileSizeBytes,
    driveUuid,
    slicingDriveUuid,
    range,
    recordingTitle,
    isPublic,
    orgId,
    getWorker,
    showToast,
  ]);

  // The size isn't optional: the WASM IndexReader is constructed with it
  // and clamps every range read against it, and the estimate that enforces
  // the output cap comes from it. No size means no slicing, rather than
  // slicing on a guess.
  if (!source || fileSizeBytes === null) return undefined;

  return {
    totalFrames: source.totalFrames,
    range,
    onRangeChange: setRange,
    presets: FRAME_PRESETS,
    sizeEstimateBytes,
    capBytes,
    onDownload: () => {
      void onDownload();
    },
    isSlicing,
    progressFraction,
    onCancel,
    isBlockedByOtherSlice,
  };
}
